import { watch } from 'vue'
import { useLDClient } from 'launchdarkly-vue-client-sdk'
import { useAuthStore } from '@/stores'
import { createUserContext } from '@/plugins/launchdarkly'
import type { UserProfileType } from '@/backend/profile.backend'

/**
 * Composable to manage LaunchDarkly context updates based on authentication state
 */
export function useLaunchDarklyContext() {
  const authStore = useAuthStore()
  
  let ldClient: any = null
  try {
    ldClient = useLDClient()
  } catch (error) {
    console.warn('LaunchDarkly client not available for context management:', error)
    return {
      updateContext: async () => {
        console.warn('LaunchDarkly client not available, cannot update context')
      }
    }
  }

  // Watch for authentication changes and update LaunchDarkly context
  watch(
    () => authStore.user,
    async (newUser, oldUser) => {
      if (!ldClient) {
        console.warn('LaunchDarkly client not available')
        return
      }

      // Only update LaunchDarkly context for authenticated users
      if (newUser) {
        try {
          const context = createUserContext(newUser)
          console.log('Updating LaunchDarkly context for authenticated user:', (newUser as any).username)
          
          await ldClient.identify(context)
          console.log('LaunchDarkly context updated successfully', context)
        } catch (error) {
          console.error('Failed to update LaunchDarkly context:', error)
        }
      } else {
        // User logged out - no LaunchDarkly context needed for unauthenticated users
        console.log('User logged out - skipping LaunchDarkly context update (not needed for unauthenticated users)')
      }
    },
    { immediate: true } // Run immediately if user is already authenticated
  )

  return {
    updateContext: async (user: UserProfileType | null) => {
      if (!ldClient) return
      
      // Only update LaunchDarkly context for authenticated users
      if (user) {
        try {
          const context = createUserContext(user)
          console.log('Manual LaunchDarkly context update for authenticated user')
          await ldClient.identify(context)
          console.log('Manual LaunchDarkly context updated successfully', context)
        } catch (error) {
          console.error('Failed to manually update LaunchDarkly context:', error)
        }
      } else {
        console.log('Manual LaunchDarkly context update skipped - no authenticated user')
      }
    }
  }
}
